import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Plus,
  Pencil,
  Trash2,
  ArrowLeft,
  Save,
  X,
  GripVertical,
  Video,
  FileText,
  HelpCircle,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const ICONS = ["BookOpen", "Flame", "Footprints", "Shield", "Recycle", "Sprout", "Leaf", "Globe", "Lightbulb", "Target"];
const COLORS = ["#C35A3A", "#4A5D4F", "#B04A2A", "#D4A5A5", "#5E7563", "#3A4A3E", "#D4785C"];

const emptyModule = {
  title: "",
  description: "",
  long_description: "",
  price: 0,
  category: "",
  external_link: "",
  icon: "BookOpen",
  color: "#C35A3A",
  content_items: [],
};

export default function AdminModules() {
  const { token } = useAuth();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // module object or null
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/modules`, { headers });
      setModules(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("Acces reserve aux administrateurs");
      }
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    if (!editing.title) { toast.error("Titre requis"); return; }
    setSaving(true);
    try {
      if (editing.id) {
        await axios.put(`${API}/admin/modules/${editing.id}`, editing, { headers });
        toast.success("Module mis a jour");
      } else {
        await axios.post(`${API}/admin/modules`, editing, { headers });
        toast.success("Module cree");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce module ?")) return;
    try {
      await axios.delete(`${API}/admin/modules/${id}`, { headers });
      toast.success("Module supprime");
      load();
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const addContentItem = (type) => {
    const item = {
      id: crypto.randomUUID(),
      type,
      title: "",
      description: "",
      url: "",
      duration: "",
      questions: type === "quiz" ? [{ q: "", options: ["", "", ""], answer: 0 }] : [],
      order: (editing.content_items?.length || 0) + 1,
    };
    setEditing({ ...editing, content_items: [...(editing.content_items || []), item] });
  };

  const updateContentItem = (idx, field, value) => {
    const items = [...editing.content_items];
    items[idx] = { ...items[idx], [field]: value };
    setEditing({ ...editing, content_items: items });
  };

  const removeContentItem = (idx) => {
    const items = editing.content_items.filter((_, i) => i !== idx);
    setEditing({ ...editing, content_items: items });
  };

  const updateQuestion = (itemIdx, qIdx, field, value) => {
    const items = [...editing.content_items];
    const questions = [...items[itemIdx].questions];
    questions[qIdx] = { ...questions[qIdx], [field]: value };
    items[itemIdx] = { ...items[itemIdx], questions };
    setEditing({ ...editing, content_items: items });
  };

  const addQuestion = (itemIdx) => {
    const items = [...editing.content_items];
    items[itemIdx].questions.push({ q: "", options: ["", "", ""], answer: 0 });
    setEditing({ ...editing, content_items: items });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bone)] pt-32 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[var(--terracotta)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Edit form
  if (editing) {
    return (
      <div className="min-h-screen bg-[var(--bone)] pt-28 pb-16" data-testid="admin-edit-module">
        <div className="container-premium max-w-3xl">
          <button onClick={() => setEditing(null)} className="inline-flex items-center gap-2 text-[var(--stone)] hover:text-[var(--terracotta)] mb-6">
            <ArrowLeft className="w-4 h-4" /> Retour
          </button>

          <h1 className="font-display text-3xl font-bold text-[var(--charcoal)] mb-8">
            {editing.id ? "Modifier le module" : "Nouveau module"}
          </h1>

          <div className="space-y-6">
            <div>
              <label className="text-sm font-medium text-[var(--charcoal)] mb-1 block">Titre</label>
              <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="input-premium" placeholder="Nom du module" data-testid="module-title-input" />
            </div>

            <div>
              <label className="text-sm font-medium text-[var(--charcoal)] mb-1 block">Description courte</label>
              <input value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="input-premium" placeholder="Description en une phrase" />
            </div>

            <div>
              <label className="text-sm font-medium text-[var(--charcoal)] mb-1 block">Description complete</label>
              <textarea value={editing.long_description} onChange={(e) => setEditing({ ...editing, long_description: e.target.value })} className="input-premium min-h-[100px] resize-y" placeholder="Description detaillee" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-[var(--charcoal)] mb-1 block">Prix (EUR)</label>
                <input type="number" value={editing.price} onChange={(e) => setEditing({ ...editing, price: parseFloat(e.target.value) || 0 })} className="input-premium" min="0" step="0.01" />
              </div>
              <div>
                <label className="text-sm font-medium text-[var(--charcoal)] mb-1 block">Categorie</label>
                <input value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="input-premium" placeholder="climat, circulaire..." />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-[var(--charcoal)] mb-1 block">Lien externe</label>
              <input value={editing.external_link} onChange={(e) => setEditing({ ...editing, external_link: e.target.value })} className="input-premium" placeholder="https://..." />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-[var(--charcoal)] mb-1 block">Couleur</label>
                <div className="flex gap-2 flex-wrap">
                  {COLORS.map((c) => (
                    <button key={c} onClick={() => setEditing({ ...editing, color: c })} className={`w-8 h-8 rounded-lg transition-transform ${editing.color === c ? "scale-125 ring-2 ring-offset-2 ring-[var(--charcoal)]" : ""}`} style={{ backgroundColor: c }} />
                  ))}
                </div>
              </div>
            </div>

            {/* Content Items */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-xl font-bold text-[var(--charcoal)]">Contenus du module</h2>
                <div className="flex gap-2">
                  <button onClick={() => addContentItem("video")} className="inline-flex items-center gap-1 text-sm bg-[var(--sand)] px-3 py-1.5 rounded-lg hover:bg-[var(--sand-dark)] transition-colors">
                    <Video className="w-3 h-3" /> Video
                  </button>
                  <button onClick={() => addContentItem("pdf")} className="inline-flex items-center gap-1 text-sm bg-[var(--sand)] px-3 py-1.5 rounded-lg hover:bg-[var(--sand-dark)] transition-colors">
                    <FileText className="w-3 h-3" /> PDF
                  </button>
                  <button onClick={() => addContentItem("quiz")} className="inline-flex items-center gap-1 text-sm bg-[var(--sand)] px-3 py-1.5 rounded-lg hover:bg-[var(--sand-dark)] transition-colors">
                    <HelpCircle className="w-3 h-3" /> Quiz
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                {(editing.content_items || []).map((item, idx) => (
                  <div key={item.id || idx} className="bg-white/60 border border-[var(--sand)] rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <GripVertical className="w-4 h-4 text-[var(--stone)]" />
                      <span className="text-xs font-bold uppercase text-[var(--terracotta)]">{item.type}</span>
                      <div className="flex-1" />
                      <button onClick={() => removeContentItem(idx)} className="text-red-400 hover:text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <input value={item.title} onChange={(e) => updateContentItem(idx, "title", e.target.value)} className="input-premium text-sm mb-2" placeholder="Titre du contenu" />
                    <input value={item.description} onChange={(e) => updateContentItem(idx, "description", e.target.value)} className="input-premium text-sm mb-2" placeholder="Description" />
                    {(item.type === "video" || item.type === "pdf") && (
                      <input value={item.url} onChange={(e) => updateContentItem(idx, "url", e.target.value)} className="input-premium text-sm mb-2" placeholder="URL du contenu" />
                    )}
                    {item.type === "video" && (
                      <input value={item.duration} onChange={(e) => updateContentItem(idx, "duration", e.target.value)} className="input-premium text-sm" placeholder="Duree (ex: 15 min)" />
                    )}
                    {item.type === "quiz" && (
                      <div className="mt-3 space-y-3">
                        {item.questions?.map((q, qIdx) => (
                          <div key={qIdx} className="bg-[var(--bone)] rounded-lg p-3">
                            <input value={q.q} onChange={(e) => updateQuestion(idx, qIdx, "q", e.target.value)} className="input-premium text-sm mb-2" placeholder={`Question ${qIdx + 1}`} />
                            {q.options?.map((opt, oIdx) => (
                              <div key={oIdx} className="flex items-center gap-2 mb-1">
                                <input type="radio" name={`answer-${idx}-${qIdx}`} checked={q.answer === oIdx} onChange={() => updateQuestion(idx, qIdx, "answer", oIdx)} />
                                <input value={opt} onChange={(e) => {
                                  const opts = [...q.options]; opts[oIdx] = e.target.value;
                                  updateQuestion(idx, qIdx, "options", opts);
                                }} className="input-premium text-sm flex-1" placeholder={`Option ${oIdx + 1}`} />
                              </div>
                            ))}
                          </div>
                        ))}
                        <button onClick={() => addQuestion(idx)} className="text-sm text-[var(--terracotta)] hover:underline">
                          + Ajouter une question
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <button onClick={handleSave} disabled={saving} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="save-module-btn">
              {saving ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><Save className="w-5 h-5" /> Enregistrer</>}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Module list
  return (
    <div className="min-h-screen bg-[var(--bone)] pt-28 pb-16" data-testid="admin-modules-page">
      <div className="container-premium">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-[var(--stone)] hover:text-[var(--terracotta)] mb-6">
          <ArrowLeft className="w-4 h-4" /> Retour au dashboard
        </Link>

        <div className="flex items-center justify-between mb-8">
          <h1 className="font-display text-3xl font-bold text-[var(--charcoal)]">
            Gestion des modules
          </h1>
          <button onClick={() => setEditing({ ...emptyModule })} className="btn-primary flex items-center gap-2 text-sm" data-testid="new-module-btn">
            <Plus className="w-4 h-4" /> Nouveau module
          </button>
        </div>

        <div className="space-y-4">
          {modules.map((m) => (
            <div key={m.id} className="card-premium p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold flex-shrink-0" style={{ backgroundColor: m.color || "var(--terracotta)" }}>
                {m.title?.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-[var(--charcoal)] truncate">{m.title}</h3>
                <p className="text-sm text-[var(--stone)] truncate">{m.description}</p>
                <div className="flex items-center gap-3 mt-1 text-xs text-[var(--stone)]">
                  <span>{m.content_items?.length || 0} contenus</span>
                  <span>{m.price > 0 ? `${m.price}EUR` : "Gratuit"}</span>
                  <span className="uppercase">{m.status}</span>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEditing({ ...m })} className="p-2 text-[var(--stone)] hover:text-[var(--terracotta)] transition-colors" data-testid={`edit-${m.id}`}>
                  <Pencil className="w-5 h-5" />
                </button>
                <button onClick={() => handleDelete(m.id)} className="p-2 text-[var(--stone)] hover:text-red-500 transition-colors" data-testid={`delete-${m.id}`}>
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}

          {modules.length === 0 && (
            <div className="text-center py-16 text-[var(--stone)]">
              <p>Aucun module pour l'instant.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
